/**
 * LUMINA — Shopify theme JS
 * Handles: header scroll, mobile menu, reveal, cart drawer (AJAX), quantity, add-to-cart
 * No dependencies. Uses Shopify AJAX API: /cart/add.js, /cart/change.js, /cart.js
 */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  // --- Header scroll ---
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => {
      if (window.scrollY > 24) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // --- Mobile menu toggle ---
  const menuToggle = $('[data-menu-toggle]');
  const mobileNav = $('[data-mobile-nav]');
  if (menuToggle && mobileNav) {
    menuToggle.addEventListener('click', () => {
      const open = mobileNav.classList.contains('is-open');
      if (open) {
        mobileNav.classList.remove('is-open','max-h-80','opacity-100');
        mobileNav.classList.add('max-h-0','opacity-0');
        menuToggle.setAttribute('aria-expanded','false');
      } else {
        mobileNav.classList.add('is-open','max-h-80','opacity-100');
        mobileNav.classList.remove('max-h-0','opacity-0');
        menuToggle.setAttribute('aria-expanded','true');
      }
      // icon swap
      const iconMenu = menuToggle.querySelector('[data-icon-menu]');
      const iconClose = menuToggle.querySelector('[data-icon-close]');
      if (iconMenu && iconClose) {
        iconMenu.classList.toggle('hidden', !open ? false : true ? false : true);
        // simpler: toggle hidden
        if (open) { iconMenu.classList.remove('hidden'); iconClose.classList.add('hidden'); }
        else { iconMenu.classList.add('hidden'); iconClose.classList.remove('hidden'); }
      }
    });
  }

  // --- Reveal on scroll ---
  const revealEls = $$('[data-reveal]');
  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          entry.target.classList.remove('translate-y-8','opacity-0');
          entry.target.classList.add('translate-y-0','opacity-100');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach(el => io.observe(el));
  } else {
    revealEls.forEach(el => el.classList.add('is-visible','translate-y-0','opacity-100'));
  }

  // --- Cart drawer helpers ---
  const drawer = $('[data-cart-drawer]');
  const drawerPanel = $('[data-cart-panel]');
  const overlay = $('[data-cart-overlay]');
  const openBtns = $$('[data-cart-open]');
  const closeBtns = $$('[data-cart-close]');
  const cartCountEls = $$('[data-cart-count]');

  function openDrawer() {
    if (!drawer || !drawerPanel) return;
    drawer.classList.remove('pointer-events-none','opacity-0');
    drawer.classList.add('pointer-events-auto','opacity-100');
    drawer.setAttribute('aria-hidden','false');
    drawerPanel.classList.remove('translate-x-full');
    drawerPanel.classList.add('translate-x-0');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    if (!drawer || !drawerPanel) return;
    drawer.classList.add('pointer-events-none','opacity-0');
    drawer.classList.remove('pointer-events-auto','opacity-100');
    drawer.setAttribute('aria-hidden','true');
    drawerPanel.classList.add('translate-x-full');
    drawerPanel.classList.remove('translate-x-0');
    document.body.style.overflow = '';
  }
  openBtns.forEach(b => b.addEventListener('click', openDrawer));
  closeBtns.forEach(b => b.addEventListener('click', closeDrawer));
  if (overlay) overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  // Expose globally for inline handlers
  window.Lumina = window.Lumina || {};
  window.Lumina.openCart = openDrawer;
  window.Lumina.closeCart = closeDrawer;

  // --- Money formatting (uses Shopify format if available) ---
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) return Shopify.formatMoney(cents, window.themeMoneyFormat || '${{amount}}');
    return '$' + (cents/100).toFixed(2);
  }

  // --- Fetch cart and update UI ---
  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
    return res.json();
  }

  function updateCartCount(count) {
    cartCountEls.forEach(el => {
      if (el === document.body) return;
      if (count > 0) {
        el.textContent = count;
        el.classList.remove('hidden');
        const badge = el.closest('[data-cart-badge]');
        if (badge && badge !== document.body) badge.classList.remove('hidden');
      } else {
        el.textContent = '0';
        el.classList.add('hidden');
      }
    });
    // Also update count inside drawer title
    const drawerCount = $('[data-drawer-count]');
    if (drawerCount) {
      drawerCount.textContent = count === 0 ? 'It is empty' : `${count} ${count===1 ? 'object' : 'objects'}`;
    }
  }

  async function refreshCartDrawer() {
    try {
      const cart = await fetchCart();
      updateCartCount(cart.item_count);

      const itemsWrap = $('[data-cart-items]');
      const emptyEl = $('[data-cart-empty]');
      const footer = $('[data-cart-footer]');
      const subtotalEl = $('[data-cart-subtotal]');
      const checkoutBtn = $('[data-cart-checkout]');

      if (!itemsWrap) return;

      if (cart.item_count === 0) {
        itemsWrap.innerHTML = '';
        if (emptyEl) emptyEl.classList.remove('hidden');
        if (footer) footer.classList.add('hidden');
        return;
      }

      if (emptyEl) emptyEl.classList.add('hidden');
      if (footer) footer.classList.remove('hidden');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (checkoutBtn) checkoutBtn.textContent = `Checkout — ${formatMoney(cart.total_price)}`;

      // Build items list from cart.js
      // cart.items has image, title, variant_title, key, quantity, final_line_price, etc.
      itemsWrap.innerHTML = cart.items.map(item => `
        <li class="flex gap-5">
          <a href="${item.url}" class="h-28 w-24 shrink-0 overflow-hidden rounded-xl shadow-softer bg-plaster-50">
            ${item.featured_image ? `<img src="${item.featured_image.url}" alt="${escapeHtml(item.title)}" class="h-full w-full object-cover" loading="lazy" width="96" height="112">` : `<div class="h-full w-full bg-plaster-200"></div>`}
          </a>
          <div class="flex flex-1 flex-col">
            <div class="flex items-start justify-between gap-3">
              <div>
                <a href="${item.url}" class="font-display text-lg leading-tight text-petrol-900 hover:text-petrol-600 transition-colors">${escapeHtml(item.product_title || item.title)}</a>
                ${item.variant_title ? `<p class="mt-0.5 text-xs uppercase tracking-[0.18em] text-stone-400">${escapeHtml(item.variant_title)}</p>` : ''}
                ${item.selling_plan_allocation ? `<p class="text-[11px] text-stone-400">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
              </div>
              <p class="text-sm text-petrol-900">${formatMoney(item.final_line_price)}</p>
            </div>
            <div class="mt-auto flex items-center justify-between">
              <div class="flex items-center clip-notch border border-plaster-300/70">
                <button data-qty-minus data-key="${item.key}" class="px-3 py-1 text-stone-500 hover:text-petrol-900" aria-label="Decrease quantity">−</button>
                <span class="w-6 text-center text-sm">${item.quantity}</span>
                <button data-qty-plus data-key="${item.key}" class="px-3 py-1 text-stone-500 hover:text-petrol-900" aria-label="Increase quantity">+</button>
              </div>
              <button data-remove data-key="${item.key}" class="text-[0.65rem] uppercase tracking-[0.2em] text-stone-400 underline-offset-4 hover:text-petrol-800 hover:underline">Remove</button>
            </div>
          </div>
        </li>
      `).join('');

      // Delegate qty handlers
      itemsWrap.querySelectorAll('[data-qty-minus]').forEach(btn => {
        btn.addEventListener('click', () => changeQty(btn.dataset.key, getQtyForKey(btn.dataset.key, cart) - 1));
      });
      itemsWrap.querySelectorAll('[data-qty-plus]').forEach(btn => {
        btn.addEventListener('click', () => changeQty(btn.dataset.key, getQtyForKey(btn.dataset.key, cart) + 1));
      });
      itemsWrap.querySelectorAll('[data-remove]').forEach(btn => {
        btn.addEventListener('click', () => changeQty(btn.dataset.key, 0));
      });

    } catch (e) {
      console.error('refreshCartDrawer', e);
    }
  }

  function getQtyForKey(key, cart) {
    const found = cart.items.find(i => i.key === key);
    return found ? found.quantity : 0;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  async function changeQty(key, qty) {
    qty = Math.max(0, parseInt(qty,10) || 0);
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    await refreshCartDrawer();
    // Dispatch event for other listeners
    document.dispatchEvent(new CustomEvent('lumina:cart:updated'));
  }

  // Poll initial
  refreshCartDrawer();

  // --- Add to cart ---
  // Handles: [data-add-to-cart] forms or buttons with data-variant-id
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    // If it's inside a form with id, let form handler take over
    if (btn.closest('form[data-product-form]')) return;

    e.preventDefault();
    const variantId = btn.getAttribute('data-variant-id') || btn.dataset.variantId;
    const qtyInput = btn.closest('[data-product-form]')?.querySelector('[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value,10) || 1 : parseInt(btn.dataset.qty || '1',10);
    if (!variantId) {
      console.warn('No variantId on add-to-cart button');
      return;
    }
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || 'Add to cart failed');
      await refreshCartDrawer();
      openDrawer();
      btn.textContent = 'In your basket';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
      btn.disabled = false;
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not add to basket');
      btn.textContent = original;
      btn.disabled = false;
    }
  });

  // Product form submit (main-product)
  $$('form[data-product-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-add-to-cart]');
      const variantId = form.querySelector('[name="id"]')?.value || form.querySelector('[data-variant-id]')?.value;
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      if (!variantId) {
        alert('Please select a variant');
        return;
      }
      if (btn) { btn.disabled = true; btn.dataset.orig = btn.textContent; btn.textContent = 'Adding…'; }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || 'Add to cart failed');
        await refreshCartDrawer();
        openDrawer();
        if (btn) { btn.textContent = 'In your basket'; setTimeout(()=> { btn.textContent = btn.dataset.orig; btn.disabled=false; }, 1600); }
      } catch (err) {
        console.error(err);
        alert(err.message || 'Could not add to basket');
        if (btn) { btn.textContent = btn.dataset.orig; btn.disabled=false; }
      }
    });
  });

  // --- Variant picker on product page ---
  // Updates hidden [name="id"], price, etc.
  $$('[data-variant-picker]').forEach(picker => {
    const form = picker.closest('form');
    const idInput = form ? form.querySelector('[name="id"]') : null;
    const priceEl = form ? form.querySelector('[data-price]') : null;
    const compareEl = form ? form.querySelector('[data-compare]') : null;
    const addBtn = form ? form.querySelector('[data-add-to-cart]') : null;
    const jsonEl = picker.querySelector('[data-variants-json]');
    if (!jsonEl) return;
    let variants = [];
    try { variants = JSON.parse(jsonEl.textContent); } catch(e) {}
    const buttons = picker.querySelectorAll('[data-option-button]');
    // Each button has data-variant-id? Actually each variant is a distinct product variant (Shopify). For LUMINA, variants are like 'Single veil' etc mapped to Shopify variants.
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        buttons.forEach(b => {
          b.classList.remove('border-petrol-900','bg-petrol-900','text-plaster-100');
          b.classList.add('border-plaster-300/70','text-petrol-700');
        });
        btn.classList.remove('border-plaster-300/70','text-petrol-700');
        btn.classList.add('border-petrol-900','bg-petrol-900','text-plaster-100');
        const vid = btn.dataset.variantId;
        if (idInput) idInput.value = vid;
        const v = variants.find(x => String(x.id) === String(vid));
        if (v && priceEl) priceEl.textContent = formatMoney(v.price);
        if (v && compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = formatMoney(v.compare_at_price);
            compareEl.classList.remove('hidden');
            const saveEl = form.querySelector('[data-save]');
            if (saveEl) { saveEl.textContent = `Save ${formatMoney(v.compare_at_price - v.price)}`; saveEl.classList.remove('hidden'); }
          } else {
            compareEl.classList.add('hidden');
            const saveEl = form.querySelector('[data-save]');
            if (saveEl) saveEl.classList.add('hidden');
          }
        }
        if (v && addBtn) {
          addBtn.disabled = !v.available;
          addBtn.textContent = v.available ? `Add to basket — ${formatMoney(v.price * (parseInt(form.querySelector('[name="quantity"]')?.value||1)))}` : 'Sold out';
        }
      });
    });

    // Quantity sync price in add button
    const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    const qtyMinus = form ? form.querySelector('[data-qty-minus]') : null;
    const qtyPlus = form ? form.querySelector('[data-qty-plus]') : null;
    const qtyDisplay = form ? form.querySelector('[data-qty-display]') : null;
    function syncQtyPrice() {
      if (!qtyInput || !addBtn) return;
      const v = variants.find(x => String(x.id) === String(idInput?.value));
      if (!v) return;
      const q = parseInt(qtyInput.value,10)||1;
      if (qtyDisplay) qtyDisplay.textContent = q;
      if (v.available) addBtn.textContent = `Add to basket — ${formatMoney(v.price * q)}`;
    }
    if (qtyMinus) qtyMinus.addEventListener('click', () => { qtyInput.value = Math.max(1, (parseInt(qtyInput.value,10)||1)-1); syncQtyPrice(); });
    if (qtyPlus) qtyPlus.addEventListener('click', () => { qtyInput.value = (parseInt(qtyInput.value,10)||1)+1; syncQtyPrice(); });
    if (qtyInput) qtyInput.addEventListener('change', syncQtyPrice);
  });

  // --- Newsletter (no backend — shows success) ---
  $$('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value.trim()) return;
      // In real shopify you'd POST to /contact#contact_form or use Shopify email
      // For now show success state
      form.classList.add('hidden');
      const success = form.parentElement.querySelector('[data-newsletter-success]');
      if (success) success.classList.remove('hidden');
      // Optionally submit to Shopify's newsletter via fetch to /contact
      // fetch('/contact#contact_form', { method: 'POST', body: new FormData(form) })
    });
  });

  // --- FAQ accordion (golden hour) ---
  $$('[data-faq-button]').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('[data-faq-item]');
      const content = item.querySelector('[data-faq-content]');
      const icon = btn.querySelector('[data-faq-icon]');
      const isOpen = item.classList.contains('is-open');
      // close all others? single open logic already in liquid but we toggle
      if (isOpen) {
        item.classList.remove('is-open','border-petrol-300/60','bg-plaster-50','shadow-softer');
        item.classList.add('border-plaster-200','bg-plaster-100/60');
        content.style.gridTemplateRows = '0fr';
        content.style.opacity = '0';
        if (icon) icon.classList.remove('rotate-45','border-petrol-600','text-petrol-900');
        if (icon) icon.classList.add('border-plaster-300','text-stone-400');
      } else {
        // optional close siblings
        const siblings = item.parentElement.querySelectorAll('[data-faq-item].is-open');
        siblings.forEach(sib => {
          sib.classList.remove('is-open','border-petrol-300/60','bg-plaster-50','shadow-softer');
          sib.classList.add('border-plaster-200','bg-plaster-100/60');
          const c = sib.querySelector('[data-faq-content]');
          if (c) { c.style.gridTemplateRows='0fr'; c.style.opacity='0'; }
          const ic = sib.querySelector('[data-faq-icon]');
          if (ic) { ic.classList.remove('rotate-45','border-petrol-600','text-petrol-900'); ic.classList.add('border-plaster-300','text-stone-400'); }
        });
        item.classList.add('is-open','border-petrol-300/60','bg-plaster-50','shadow-softer');
        item.classList.remove('border-plaster-200','bg-plaster-100/60');
        content.style.gridTemplateRows = '1fr';
        content.style.opacity = '1';
        if (icon) { icon.classList.add('rotate-45','border-petrol-600','text-petrol-900'); icon.classList.remove('border-plaster-300','text-stone-400'); }
      }
    });
  });

  // --- Product accordion ---
  $$('[data-accordion-button]').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('[data-accordion-item]');
      const content = item.querySelector('[data-accordion-content]');
      const icon = btn.querySelector('[data-accordion-icon]');
      const isOpen = item.classList.contains('is-open');
      if (isOpen) {
        item.classList.remove('is-open');
        content.style.gridTemplateRows = '0fr';
        content.style.opacity = '0';
        if (icon) icon.classList.remove('rotate-45');
      } else {
        item.classList.add('is-open');
        content.style.gridTemplateRows = '1fr';
        content.style.opacity = '1';
        if (icon) icon.classList.add('rotate-45');
      }
    });
  });

  // Expose refresh for external calls
  window.Lumina.refreshCart = refreshCartDrawer;

})();
